import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LiveKitRoom,
  ParticipantTile,
  RoomAudioRenderer,
  TrackToggle,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  useSpeakingParticipants,
  useTracks,
} from '@livekit/components-react';
import { RoomEvent, Track } from 'livekit-client';
import { BackgroundProcessor } from '@livekit/track-processors';
import '@livekit/components-styles';

/**
 * FinCollab conference UI
 *
 * The LiveKit connection contract intentionally remains the same:
 *   open, onClose, token, serverUrl
 *
 * The media layer is still handled by LiveKit; this component only
 * replaces the stock VideoConference UI with a responsive FinCollab UI.
 */

const Icon = ({ name, size = 22 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  const paths = {
    mic: (
      <>
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <path d="M12 18v3" />
        <path d="M8 21h8" />
      </>
    ),
    micOff: (
      <>
        <path d="M9 9V6a3 3 0 0 1 5.2-2" />
        <path d="M15 10V6" />
        <path d="M5 11a7 7 0 0 0 11.2 5.6" />
        <path d="M19 11a7 7 0 0 1-.7 3" />
        <path d="M12 18v3" />
        <path d="M8 21h8" />
        <path d="m4 4 16 16" />
      </>
    ),
    camera: (
      <>
        <rect x="3" y="7" width="13" height="10" rx="2" />
        <path d="m16 10 5-3v10l-5-3" />
      </>
    ),
    cameraOff: (
      <>
        <path d="M3 7h8" />
        <path d="M13 7h3v10H7" />
        <path d="m16 10 5-3v10l-5-3" />
        <path d="m4 4 16 16" />
      </>
    ),
    screen: (
      <>
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
      </>
    ),
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="9.5" cy="7" r="4" />
        <path d="M17 11a4 4 0 0 0 0-8" />
        <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
      </>
    ),
    chat: (
      <>
        <path d="M20 11.5a7.5 7.5 0 0 1-8 7.5 8.8 8.8 0 0 1-3.2-.6L4 20l1.6-3.7A7.4 7.4 0 0 1 4.5 12 7.5 7.5 0 0 1 12 4.5h.5A7.5 7.5 0 0 1 20 11.5Z" />
        <path d="M8 12h.01M12 12h.01M16 12h.01" />
      </>
    ),
    more: (
      <>
        <circle cx="5" cy="12" r="1" fill="currentColor" />
        <circle cx="12" cy="12" r="1" fill="currentColor" />
        <circle cx="19" cy="12" r="1" fill="currentColor" />
      </>
    ),
    close: (
      <>
        <path d="m6 6 12 12" />
        <path d="m18 6-12 12" />
      </>
    ),
    phone: (
      <>
        <path d="M7.5 4.5 5 6c-.8.5-.9 1.6-.5 2.5 2 4.7 6.3 9 11 11 .9.4 2 .3 2.5-.5l1.5-2.5-4-2-1.6 1.6a15 15 0 0 1-6-6L9.5 9z" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
  };

  return <svg {...common}>{paths[name] || paths.more}</svg>;
};

const BackgroundBlur = () => {
  const { localParticipant } = useLocalParticipant();
  const [blurred, setBlurred] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const toggleBlur = async () => {
    if (busyRef.current) return;
    const publication = localParticipant.getTrackPublication(Track.Source.Camera);
    if (!publication?.track) return;

    busyRef.current = true;
    setBusy(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (blurred) {
        await publication.track.stopProcessor();
      } else {
        const processor = BackgroundProcessor({ mode: 'background-blur', blurRadius: 10 });
        await publication.track.setProcessor(processor);
      }
      setBlurred((value) => !value);
    } catch (error) {
      console.error('Ошибка изменения размытия фона:', error);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const label = busy
    ? (blurred ? 'Отключение размытия…' : 'Размытие…')
    : (blurred ? 'Размытие включено' : 'Размыть фон');

  return (
    <button
      type="button"
      className={`finconf-more-action ${blurred ? 'is-active' : ''} ${busy ? 'is-busy' : ''}`}
      onClick={toggleBlur}
      disabled={busy}
      title={label}
      aria-label={label}
      aria-busy={busy}
    >
      <span className={`finconf-more-icon ${busy ? 'is-spinning' : ''}`}>
        {busy ? '◌' : (blurred ? '✓' : '✦')}
      </span>
      <span>{label}</span>
    </button>
  );
};

const ParticipantCard = ({ trackRef, isSpeaking }) => (
  <div className={`finconf-participant ${isSpeaking ? 'is-speaking' : ''}`}>
    <ParticipantTile
      trackRef={trackRef}
      disableSpeakingIndicator
      className="finconf-participant-tile"
    />
    <div className="finconf-participant-gradient" />
  </div>
);

const ParticipantGrid = () => {
  const participants = useParticipants();
  const speakingParticipants = useSpeakingParticipants();
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  const screenTracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }], { onlySubscribed: false });
  const participantMap = useMemo(() => new Set(speakingParticipants.map((p) => p.identity)), [speakingParticipants]);
  const screenTrack = screenTracks[0] || null;

  const cameraTracksByIdentity = useMemo(() => {
    const map = new Map();
    cameraTracks.forEach((trackRef) => {
      const identity = trackRef.participant?.identity;
      if (identity && !map.has(identity)) map.set(identity, trackRef);
    });
    return map;
  }, [cameraTracks]);

  const cameraParticipants = useMemo(() => participants.map((participant) => ({
    participant,
    trackRef: cameraTracksByIdentity.get(participant.identity) || {
      participant, source: Track.Source.Camera, publication: undefined,
    },
  })), [participants, cameraTracksByIdentity]);

  if (screenTrack) {
    return (
      <div className="finconf-stage-layout">
        <div className="finconf-screen-stage">
          <ParticipantCard trackRef={screenTrack} isSpeaking={participantMap.has(screenTrack.participant?.identity)} />
          <div className="finconf-screen-share-badge">
            <Icon name="screen" size={16} />
            <span>{screenTrack.participant?.name || screenTrack.participant?.identity || 'Демонстрация экрана'}</span>
          </div>
        </div>
        <div className="finconf-filmstrip">
          {cameraParticipants.map(({ participant, trackRef }) => (
            <div className="finconf-filmstrip-item" key={participant.identity}>
              <ParticipantCard trackRef={trackRef} isSpeaking={participantMap.has(participant.identity)} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const count = cameraParticipants.length;
  return (
    <div className={`finconf-grid finconf-grid-count-${Math.min(Math.max(count, 1), 9)}`}>
      {cameraParticipants.map(({ participant, trackRef }) => (
        <ParticipantCard key={participant.identity} trackRef={trackRef} isSpeaking={participantMap.has(participant.identity)} />
      ))}
    </div>
  );
};

const ControlButton = ({
  label,
  icon,
  active = false,
  danger = false,
  onClick,
  children,
  title,
}) => (
  <div className="finconf-control-wrap">
    <button
      type="button"
      className={[
        'finconf-control',
        active ? 'is-active' : '',
        danger ? 'is-danger' : '',
      ].join(' ')}
      onClick={onClick}
      title={title || label}
      aria-label={label}
    >
      {children || <Icon name={icon} />}
    </button>
    <span className="finconf-control-label">{label}</span>
  </div>
);

const MediaControlButton = ({ source, label, enabled, onChange }) => {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;

    setBusy(true);
    try {
      await onChange(!enabled);
    } catch (error) {
      console.error(`Ошибка переключения: ${label}`, error);
    } finally {
      setBusy(false);
    }
  };

  const isMicrophone = source === Track.Source.Microphone;
  const icon = isMicrophone
    ? (enabled ? 'mic' : 'micOff')
    : (enabled ? 'camera' : 'cameraOff');
  const visibleLabel = busy
    ? 'Изменение…'
    : (enabled ? label : `${label} выключен`);

  return (
    <div className="finconf-control-wrap">
      <button
        type="button"
        className={[
          'finconf-control',
          !enabled ? 'is-off' : '',
          busy ? 'is-busy' : '',
        ].join(' ')}
        onClick={toggle}
        disabled={busy}
        title={enabled ? `Выключить: ${label.toLowerCase()}` : `Включить: ${label.toLowerCase()}`}
        aria-label={visibleLabel}
        aria-pressed={enabled}
      >
        <Icon name={icon} />
        {!enabled && <span className="finconf-control-off-mark">×</span>}
      </button>
      <span className={`finconf-control-label ${!enabled ? 'is-off' : ''}`}>
        {visibleLabel}
      </span>
    </div>
  );
};

const ConferenceControls = ({ onClose, onTogglePanel, showPanel }) => {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const [screenSharing, setScreenSharing] = useState(localParticipant.isScreenShareEnabled);

  useEffect(() => {
    setScreenSharing(localParticipant.isScreenShareEnabled);
  }, [localParticipant]);

  const toggleMicrophone = (enabled) => localParticipant.setMicrophoneEnabled(enabled);
  const toggleCamera = (enabled) => localParticipant.setCameraEnabled(enabled);

  return (
    <div className="finconf-controls-shell">
      <div className="finconf-controls">
        <MediaControlButton
          source={Track.Source.Microphone}
          label="Микрофон"
          enabled={isMicrophoneEnabled}
          onChange={toggleMicrophone}
        />

        <MediaControlButton
          source={Track.Source.Camera}
          label="Камера"
          enabled={isCameraEnabled}
          onChange={toggleCamera}
        />

        <div className="finconf-control-wrap">
          <TrackToggle
            source={Track.Source.ScreenShare}
            showIcon={false}
            className={`finconf-control finconf-track-toggle ${screenSharing ? 'is-active' : ''}`}
            aria-label="Демонстрация экрана"
            title={screenSharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'}
            onChange={(enabled) => setScreenSharing(enabled)}
          >
            <Icon name="screen" />
          </TrackToggle>
          <span className="finconf-control-label">Экран</span>
        </div>

        <ControlButton
          label="Участники"
          icon="users"
          active={showPanel === 'participants'}
          onClick={() => onTogglePanel('participants')}
        />

        <ControlButton
          label="Чат"
          icon="chat"
          active={showPanel === 'chat'}
          onClick={() => onTogglePanel('chat')}
        />

        <ControlButton
          label="Ещё"
          icon="more"
          active={showPanel === 'more'}
          onClick={() => onTogglePanel('more')}
        />

        <ControlButton
          label="Выйти"
          icon="phone"
          danger
          onClick={onClose}
          title="Выйти из конференции"
        />
      </div>
    </div>
  );
};

const ParticipantsPanel = ({ participants }) => (
  <div className="finconf-side-content">
    <div className="finconf-participants-list">
      {participants.map((participant) => (
        <div className="finconf-participant-row" key={participant.identity}>
          <div className="finconf-avatar">
            {(participant.name || participant.identity || '?')
              .trim()
              .charAt(0)
              .toUpperCase()}
          </div>
          <div className="finconf-participant-info">
            <span>{participant.name || participant.identity}</span>
            {participant.isLocal && <small>Вы</small>}
          </div>
          <div className="finconf-participant-media" aria-label="Состояние медиа">
            <Icon
              name={participant.isMicrophoneEnabled ? 'mic' : 'micOff'}
              size={17}
            />
            <Icon
              name={participant.isCameraEnabled ? 'camera' : 'cameraOff'}
              size={17}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ConferenceChatPanel = () => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handleDataReceived = (payload, participant, _kind, topic) => {
      if (topic && topic !== 'finconf-chat') return;

      try {
        const decoded = new TextDecoder().decode(payload);
        const data = JSON.parse(decoded);
        if (data?.type !== 'finconf-chat' || !data.message) return;

        const messageId = data.id || `${participant?.identity || 'unknown'}-${data.timestamp || Date.now()}`;
        setMessages((current) => {
          if (current.some((item) => item.id === messageId)) return current;
          return [
            ...current,
            {
              id: messageId,
              message: String(data.message),
              timestamp: Number(data.timestamp) || Date.now(),
              identity: participant?.identity || 'unknown',
              name: participant?.name || participant?.identity || 'Участник',
              isLocal: false,
            },
          ];
        });
      } catch (error) {
        console.error('Ошибка чтения сообщения конференционного чата:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);
    return () => room.off(RoomEvent.DataReceived, handleDataReceived);
  }, [room]);

  useEffect(() => {
    const node = document.querySelector('.finconf-chat-messages');
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    const message = draft.trim();
    if (!message || sending) return;

    setSending(true);
    const timestamp = Date.now();
    const id = `${localParticipant.identity}-${timestamp}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      const payload = new TextEncoder().encode(JSON.stringify({
        type: 'finconf-chat',
        id,
        message: message.slice(0, 1000),
        timestamp,
      }));

      await localParticipant.publishData(payload, {
        reliable: true,
        topic: 'finconf-chat',
      });

      setMessages((current) => [
        ...current,
        {
          id,
          message,
          timestamp,
          identity: localParticipant.identity,
          name: localParticipant.name || localParticipant.identity || 'Вы',
          isLocal: true,
        },
      ]);
      setDraft('');
    } catch (error) {
      console.error('Ошибка отправки сообщения конференционного чата:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) =>
    new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(timestamp);

  return (
    <div className="finconf-chat">
      <div className="finconf-chat-messages" aria-live="polite">
        {messages.length === 0 ? (
          <div className="finconf-chat-empty">
            <div className="finconf-chat-empty-icon">
              <Icon name="chat" size={24} />
            </div>
            <strong>Чат конференции</strong>
            <span>Сообщения видны участникам этого созвона и не сохраняются после его завершения.</span>
          </div>
        ) : (
          messages.map((item) => (
            <div
              className={`finconf-chat-message ${item.isLocal ? 'is-local' : ''}`}
              key={item.id}
            >
              <div className="finconf-chat-message-meta">
                <strong>{item.isLocal ? 'Вы' : item.name}</strong>
                <time>{formatTime(item.timestamp)}</time>
              </div>
              <div className="finconf-chat-bubble">{item.message}</div>
            </div>
          ))
        )}
      </div>

      <div className="finconf-chat-composer">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, 1000))}
          onKeyDown={handleKeyDown}
          placeholder="Сообщение..."
          rows={1}
          aria-label="Сообщение в чат конференции"
        />
        <button
          type="button"
          className="finconf-chat-send"
          onClick={sendMessage}
          disabled={!draft.trim() || sending}
          aria-label="Отправить сообщение"
          title="Отправить сообщение"
        >
          ➤
        </button>
      </div>
    </div>
  );
};

const SidePanel = ({ panel, participants, onClose, onChangePanel }) => (
  <aside className="finconf-side-panel">
    <div className="finconf-panel-header">
      <div className="finconf-panel-tabs">
        <button
          type="button"
          className={`finconf-panel-tab ${panel === 'participants' ? 'is-active' : ''}`}
          onClick={() => onChangePanel('participants')}
        >
          <Icon name="users" size={18} />
          <span>Участники</span>
          <b>{participants.length}</b>
        </button>
        <button
          type="button"
          className={`finconf-panel-tab ${panel === 'chat' ? 'is-active' : ''}`}
          onClick={() => onChangePanel('chat')}
        >
          <Icon name="chat" size={18} />
          <span>Чат</span>
        </button>
      </div>

      <button
        type="button"
        className="finconf-panel-close"
        onClick={onClose}
        aria-label="Закрыть панель"
      >
        <Icon name="close" size={20} />
      </button>
    </div>

    {panel === 'participants' ? (
      <ParticipantsPanel participants={participants} />
    ) : (
      <ConferenceChatPanel />
    )}
  </aside>
);

const MorePanel = ({ onClose }) => (
  <aside className="finconf-side-panel finconf-more-panel">
    <div className="finconf-panel-header">
      <div>
        <strong>Дополнительно</strong>
      </div>
      <button
        type="button"
        className="finconf-panel-close"
        onClick={onClose}
        aria-label="Закрыть панель"
      >
        <Icon name="close" size={20} />
      </button>
    </div>

    <div className="finconf-more-actions">
      <BackgroundBlur />
    </div>
  </aside>
);

const FinCollabConference = ({ onClose }) => {
  const participants = useParticipants();
  const [panel, setPanel] = useState(null);
  const [controlsVisible, setControlsVisible] = useState(true);

  useEffect(() => {
    let timer;

    const scheduleHide = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!panel) {
          setControlsVisible(false);
        }
      }, 4500);
    };

    const handleActivity = () => {
      setControlsVisible(true);
      scheduleHide();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity);

    scheduleHide();

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [panel]);

  const togglePanel = (nextPanel) => {
    setPanel((current) => (current === nextPanel ? null : nextPanel));
    setControlsVisible(true);
  };

  return (
    <div
      className="finconf"
      onClick={() => {
        if (!controlsVisible) setControlsVisible(true);
      }}
    >
      <style>{`
        .finconf {
          --fc-bg: #08090c;
          --fc-panel: rgba(25, 27, 33, .72);
          --fc-panel-strong: rgba(30, 32, 39, .9);
          --fc-border: rgba(255, 255, 255, .11);
          --fc-text: rgba(255, 255, 255, .96);
          --fc-muted: rgba(255, 255, 255, .58);
          --fc-accent: #ffffff;
          --fc-danger: #ff453a;
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 100vh;
          overflow: hidden;
          background:
            radial-gradient(circle at 50% -10%, rgba(255,255,255,.09), transparent 35%),
            var(--fc-bg);
          color: var(--fc-text);
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
            "SF Pro Text", Inter, system-ui, sans-serif;
          -webkit-font-smoothing: antialiased;
          isolation: isolate;
        }

        .finconf * {
          box-sizing: border-box;
        }

        .finconf-header {
          position: absolute;
          top: max(16px, env(safe-area-inset-top));
          left: 16px;
          right: 16px;
          z-index: 30;
          display: flex;
          justify-content: space-between;
          align-items: center;
          pointer-events: none;
        }

        .finconf-brand,
        .finconf-status {
          pointer-events: auto;
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 42px;
          padding: 9px 14px;
          border: 1px solid var(--fc-border);
          border-radius: 18px;
          background: rgba(22, 24, 29, .58);
          backdrop-filter: blur(24px) saturate(150%);
          -webkit-backdrop-filter: blur(24px) saturate(150%);
          box-shadow: 0 12px 35px rgba(0,0,0,.18);
        }

        .finconf-brand {
          font-weight: 650;
          letter-spacing: -.02em;
        }

        .finconf-brand-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #34c759;
          box-shadow: 0 0 14px rgba(52,199,89,.55);
        }

        .finconf-status {
          color: var(--fc-muted);
          font-size: 13px;
        }

        .finconf-grid {
          position: absolute;
          inset: 0;
          display: grid;
          gap: 10px;
          padding: 76px 12px 112px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-auto-rows: minmax(0, 1fr);
        }

        .finconf-stage-layout { position: absolute; inset: 0; display: grid; grid-template-rows: minmax(0, 1fr) 118px; gap: 10px; padding: 76px 12px 112px; }
        .finconf-screen-stage { position: relative; min-width: 0; min-height: 0; overflow: hidden; border: 1px solid rgba(255,255,255,.1); border-radius: 22px; background: #111216; box-shadow: 0 18px 55px rgba(0,0,0,.28); }
        .finconf-screen-stage > .finconf-participant { position: absolute; inset: 0; border: 0; border-radius: inherit; box-shadow: none; }
        .finconf-screen-stage .finconf-participant-tile video { object-fit: contain; background: #050506; }
        .finconf-screen-share-badge { position: absolute; left: 14px; bottom: 14px; z-index: 5; display: flex; align-items: center; gap: 8px; max-width: min(70vw, 420px); padding: 9px 12px; border: 1px solid rgba(255,255,255,.12); border-radius: 14px; color: rgba(255,255,255,.92); background: rgba(18,20,25,.72); backdrop-filter: blur(18px) saturate(145%); -webkit-backdrop-filter: blur(18px) saturate(145%); box-shadow: 0 10px 30px rgba(0,0,0,.24); font-size: 12px; }
        .finconf-screen-share-badge span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .finconf-filmstrip { display: flex; gap: 8px; min-width: 0; min-height: 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: none; }
        .finconf-filmstrip::-webkit-scrollbar { display: none; }
        .finconf-filmstrip-item { flex: 0 0 150px; min-width: 0; min-height: 0; }
        .finconf-filmstrip-item .finconf-participant { width: 100%; height: 100%; border-radius: 17px; }

        .finconf-grid-count-1 {
          grid-template-columns: minmax(0, 1fr);
        }

        .finconf-grid-count-3,
        .finconf-grid-count-4 {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .finconf-grid-count-5,
        .finconf-grid-count-6,
        .finconf-grid-count-7,
        .finconf-grid-count-8,
        .finconf-grid-count-9 {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .finconf-participant {
          position: relative;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 22px;
          background: linear-gradient(145deg, #202229, #111216);
          box-shadow: 0 18px 45px rgba(0,0,0,.24);
          transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
        }

        .finconf-participant.is-speaking {
          border-color: rgba(255,255,255,.72);
          box-shadow:
            0 0 0 2px rgba(255,255,255,.16),
            0 18px 45px rgba(0,0,0,.3);
        }

        .finconf-participant-tile {
          position: absolute !important;
          inset: 0 !important;
          width: 100% !important;
          height: 100% !important;
          border-radius: inherit !important;
          overflow: hidden !important;
        }

        .finconf-participant-tile video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .finconf-participant-gradient {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 34%;
          pointer-events: none;
          background: linear-gradient(transparent, rgba(0,0,0,.62));
        }

        .finconf-controls-shell {
          position: absolute;
          left: 50%;
          bottom: max(18px, env(safe-area-inset-bottom));
          z-index: 40;
          transform: translateX(-50%);
          transition: opacity .25s ease, transform .25s ease;
        }

        .finconf-controls-shell.is-hidden {
          opacity: 0;
          pointer-events: none;
          transform: translate(-50%, 18px);
        }

        .finconf-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 26px;
          background: rgba(24, 26, 32, .72);
          backdrop-filter: blur(28px) saturate(155%);
          -webkit-backdrop-filter: blur(28px) saturate(155%);
          box-shadow:
            0 22px 60px rgba(0,0,0,.4),
            inset 0 1px rgba(255,255,255,.07);
        }

        .finconf-control-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .finconf-control {
          width: 52px;
          height: 52px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 1px solid rgba(255,255,255,.09);
          border-radius: 17px;
          color: rgba(255,255,255,.92);
          background: rgba(255,255,255,.075);
          cursor: pointer;
          transition: transform .15s ease, background .15s ease;
          -webkit-tap-highlight-color: transparent;
        }

        .finconf-control:hover {
          background: rgba(255,255,255,.14);
          transform: translateY(-1px);
        }

        /* LiveKit TrackToggle adds its own button styles; normalize it to the same
           visual contract as the neighboring FinCollab controls. */
        .finconf .finconf-track-toggle {
          box-sizing: border-box !important;
          width: 52px !important;
          height: 52px !important;
          min-width: 52px !important;
          min-height: 52px !important;
          display: grid !important;
          place-items: center !important;
          padding: 0 !important;
          border: 1px solid rgba(255,255,255,.09) !important;
          border-radius: 17px !important;
          color: rgba(255,255,255,.92) !important;
          background: rgba(255,255,255,.075) !important;
          box-shadow: none !important;
          cursor: pointer;
          transition: transform .15s ease, background .15s ease;
        }

        .finconf .finconf-track-toggle:hover {
          background: rgba(255,255,255,.14) !important;
          transform: translateY(-1px);
        }

        .finconf .finconf-track-toggle.is-active {
          background: rgba(255,255,255,.18) !important;
        }

        .finconf-control:active {
          transform: scale(.94);
        }

        .finconf-control.is-active {
          background: rgba(255,255,255,.18);
        }

        .finconf-control.is-danger {
          color: #fff;
          background: var(--fc-danger);
          border-color: transparent;
        }

        .finconf-control.is-off {
          color: #ff6961;
          background: rgba(255,69,58,.18);
          border-color: rgba(255,69,58,.36);
          box-shadow: inset 0 0 0 1px rgba(255,69,58,.05);
        }

        .finconf-control.is-off:hover {
          background: rgba(255,69,58,.25);
        }

        .finconf-control-off-mark {
          position: absolute;
          right: 7px;
          bottom: 6px;
          width: 15px;
          height: 15px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          color: #fff;
          background: #ff453a;
          font-size: 12px;
          font-weight: 800;
          line-height: 1;
          box-shadow: 0 3px 10px rgba(0,0,0,.3);
        }

        .finconf-control-wrap {
          position: relative;
        }

        .finconf-control:disabled {
          opacity: .7;
          cursor: wait;
          transform: none;
        }

        .finconf-control-label {
          color: rgba(255,255,255,.55);
          font-size: 10px;
          line-height: 1;
          white-space: nowrap;
          user-select: none;
        }

        .finconf-track-toggle {
          width: 100%;
          height: 100%;
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          border-radius: inherit;
          color: inherit;
          background: transparent;
          cursor: pointer;
        }

        .finconf-side-panel {
          position: absolute;
          display: flex;
          flex-direction: column;
          top: 74px;
          right: 16px;
          bottom: 96px;
          z-index: 50;
          width: min(360px, calc(100vw - 32px));
          overflow: hidden;
          border: 1px solid var(--fc-border);
          border-radius: 26px;
          background: rgba(22,24,30,.82);
          backdrop-filter: blur(30px) saturate(150%);
          -webkit-backdrop-filter: blur(30px) saturate(150%);
          box-shadow: 0 28px 70px rgba(0,0,0,.4);
        }

        .finconf-panel-header {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 18px;
          border-bottom: 1px solid rgba(255,255,255,.08);
        }

        .finconf-panel-header > div {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .finconf-panel-tabs {
          display: flex !important;
          align-items: center;
          gap: 5px !important;
          min-width: 0;
        }

        .finconf-panel-tab {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 10px;
          border: 1px solid transparent;
          border-radius: 13px;
          color: rgba(255,255,255,.55);
          background: transparent;
          cursor: pointer;
          font: inherit;
          white-space: nowrap;
        }

        .finconf-panel-tab.is-active {
          color: rgba(255,255,255,.96);
          background: rgba(255,255,255,.09);
          border-color: rgba(255,255,255,.08);
        }

        .finconf-panel-tab b {
          min-width: 22px;
          height: 22px;
          display: grid;
          place-items: center;
          padding: 0 6px;
          border-radius: 999px;
          color: rgba(255,255,255,.72);
          background: rgba(255,255,255,.08);
          font-size: 11px;
          font-weight: 600;
        }

        .finconf-participant-media {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(255,255,255,.72);
        }

        .finconf-participant-media svg {
          opacity: .9;
        }

        .finconf-participant-media svg:first-child {
          color: rgba(255,255,255,.9);
        }

        .finconf-participant-media svg:last-child {
          color: rgba(255,255,255,.7);
        }


        .finconf-panel-close {
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 13px;
          color: rgba(255,255,255,.72);
          background: rgba(255,255,255,.08);
          cursor: pointer;
        }

        .finconf-participants-list {
          height: 100%;
          min-height: 0;
          overflow: auto;
          padding: 10px;
        }

        .finconf-participant-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          border-radius: 15px;
        }

        .finconf-participant-row:hover {
          background: rgba(255,255,255,.06);
        }

        .finconf-avatar {
          width: 40px;
          height: 40px;
          flex: 0 0 40px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          background: linear-gradient(145deg, #5d626d, #30333a);
          color: #fff;
          font-weight: 650;
        }

        .finconf-participant-info {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .finconf-participant-info span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .finconf-participant-info small {
          color: var(--fc-muted);
          font-size: 11px;
        }

        .finconf-side-content {
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
        }

        .finconf-chat {
          flex: 1 1 auto;
          height: auto;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .finconf-chat-messages {
          flex: 1;
          min-height: 0;
          overflow: auto;
          padding: 12px;
          scrollbar-width: thin;
        }

        .finconf-chat-empty {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 30px;
          text-align: center;
          color: rgba(255,255,255,.56);
        }

        .finconf-chat-empty strong {
          color: rgba(255,255,255,.9);
          font-size: 14px;
        }

        .finconf-chat-empty span {
          max-width: 270px;
          font-size: 12px;
          line-height: 1.5;
        }

        .finconf-chat-empty-icon {
          width: 54px;
          height: 54px;
          display: grid;
          place-items: center;
          margin-bottom: 4px;
          border-radius: 18px;
          color: rgba(255,255,255,.82);
          background: rgba(255,255,255,.08);
        }

        .finconf-chat-message {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          margin-bottom: 12px;
        }

        .finconf-chat-message.is-local {
          align-items: flex-end;
        }

        .finconf-chat-message-meta {
          display: flex;
          align-items: baseline;
          gap: 7px;
          max-width: 88%;
          color: rgba(255,255,255,.52);
          font-size: 10px;
        }

        .finconf-chat-message-meta strong {
          color: rgba(255,255,255,.78);
          font-size: 11px;
        }

        .finconf-chat-message-meta time {
          white-space: nowrap;
        }

        .finconf-chat-bubble {
          max-width: 88%;
          padding: 9px 11px;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 14px 14px 14px 5px;
          color: rgba(255,255,255,.9);
          background: rgba(255,255,255,.07);
          font-size: 13px;
          line-height: 1.4;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
        }

        .finconf-chat-message.is-local .finconf-chat-bubble {
          border-radius: 14px 14px 5px 14px;
          color: #fff;
          background: rgba(255,255,255,.14);
        }

        .finconf-chat-composer {
          flex: 0 0 auto;
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 10px;
          border-top: 1px solid rgba(255,255,255,.08);
          background: rgba(16,18,23,.45);
        }

        .finconf-chat-composer textarea {
          flex: 1;
          min-width: 0;
          max-height: 90px;
          resize: none;
          padding: 10px 12px;
          border: 1px solid rgba(255,255,255,.1);
          border-radius: 14px;
          outline: none;
          color: rgba(255,255,255,.94);
          background: rgba(255,255,255,.07);
          font: inherit;
          font-size: 13px;
          line-height: 1.35;
        }

        .finconf-chat-composer textarea:focus {
          border-color: rgba(255,255,255,.24);
          background: rgba(255,255,255,.09);
        }

        .finconf-chat-composer textarea::placeholder {
          color: rgba(255,255,255,.4);
        }

        .finconf-chat-send {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: grid;
          place-items: center;
          border: 0;
          border-radius: 13px;
          color: #111216;
          background: #fff;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
        }

        .finconf-chat-send:disabled {
          opacity: .35;
          cursor: default;
        }

        .finconf-more-actions {
          padding: 12px;
        }

        .finconf-more-action {
          width: 100%;
          min-height: 52px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 16px;
          color: rgba(255,255,255,.9);
          background: rgba(255,255,255,.06);
          cursor: pointer;
          font: inherit;
          text-align: left;
        }

        .finconf-more-action.is-active {
          background: rgba(255,255,255,.13);
        }
        .finconf-more-action:disabled { opacity: .72; cursor: wait; transform: none; }
        .finconf-more-action.is-busy { background: rgba(255,255,255,.1); }
        .finconf-more-icon.is-spinning { animation: finconf-spin .8s linear infinite; }
        @keyframes finconf-spin { to { transform: rotate(360deg); } }

        .finconf-more-icon {
          width: 28px;
          height: 28px;
          display: grid;
          place-items: center;
          border-radius: 9px;
          background: rgba(255,255,255,.1);
        }

        @media (max-width: 900px) {
          .finconf-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .finconf-grid-count-5,
          .finconf-grid-count-6,
          .finconf-grid-count-7,
          .finconf-grid-count-8,
          .finconf-grid-count-9 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .finconf-control {
            width: 48px;
            height: 48px;
          }

          .finconf .finconf-track-toggle {
            width: 48px !important;
            height: 48px !important;
            min-width: 48px !important;
            min-height: 48px !important;
          }

          .finconf-control-label {
            display: none;
          }

          .finconf-controls {
            padding: 8px;
            border-radius: 23px;
          }
        }

        @media (max-width: 900px) {
          .finconf-stage-layout { grid-template-rows: minmax(0, 1fr) 100px; }
          .finconf-filmstrip-item { flex-basis: 128px; }
        }

        @media (max-width: 600px) {
          .finconf-header {
            left: 10px;
            right: 10px;
          }

          .finconf-brand,
          .finconf-status {
            min-height: 38px;
            padding: 8px 11px;
            border-radius: 15px;
          }

          .finconf-status {
            max-width: 45vw;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .finconf-grid {
            gap: 7px;
            padding: 62px 7px 92px;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .finconf-stage-layout { grid-template-rows: minmax(0, 1fr) 82px; gap: 7px; padding: 62px 7px 92px; }
          .finconf-screen-stage { border-radius: 15px; }
          .finconf-filmstrip { gap: 6px; }
          .finconf-filmstrip-item { flex-basis: 104px; }
          .finconf-screen-share-badge { left: 9px; bottom: 9px; max-width: 70vw; padding: 7px 9px; border-radius: 11px; font-size: 11px; }

          .finconf-grid-count-1 {
            grid-template-columns: 1fr;
          }

          .finconf-grid-count-3 {
            grid-template-columns: 1fr;
          }

          .finconf-grid-count-5,
          .finconf-grid-count-6,
          .finconf-grid-count-7,
          .finconf-grid-count-8,
          .finconf-grid-count-9 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .finconf-participant {
            border-radius: 15px;
          }

          .finconf-controls-shell {
            left: 8px;
            right: 8px;
            bottom: max(8px, env(safe-area-inset-bottom));
            transform: none;
          }

          .finconf-controls-shell.is-hidden {
            transform: translateY(18px);
          }

          .finconf-controls {
            width: 100%;
            justify-content: space-between;
            gap: 5px;
            padding: 7px;
            border-radius: 22px;
          }

          .finconf-control {
            width: clamp(44px, 13vw, 54px);
            height: clamp(44px, 13vw, 54px);
            border-radius: 15px;
          }

          .finconf-side-panel {
            top: auto;
            left: 7px;
            right: 7px;
            bottom: max(78px, calc(70px + env(safe-area-inset-bottom)));
            width: auto;
            max-height: min(70vh, 560px);
            border-radius: 24px;
          }

          .finconf-panel-tab {
            padding-left: 8px;
            padding-right: 8px;
          }

          .finconf-panel-tab span {
            display: none;
          }

          .finconf-panel-tab b {
            display: grid;
          }

          .finconf-participant-media {
            gap: 4px;
          }
        }

        @media (orientation: landscape) and (max-height: 600px) {
          .finconf-grid {
            padding-top: 10px;
            padding-bottom: 72px;
          }

          .finconf-header {
            top: 8px;
          }

          .finconf-controls-shell {
            bottom: 8px;
          }

          .finconf-control {
            width: 44px;
            height: 44px;
          }

          .finconf .finconf-track-toggle {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px !important;
            min-height: 44px !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .finconf *,
          .finconf *::before,
          .finconf *::after {
            transition: none !important;
            animation: none !important;
          }
        }
      `}</style>

      <div className="finconf-header">
        <div className="finconf-brand">
          <span className="finconf-brand-dot" />
          <span>FinCollab</span>
        </div>
        <div className="finconf-status">
          <span>В конференции</span>
        </div>
      </div>

      <ParticipantGrid />

      <RoomAudioRenderer />

      <div
        className={`finconf-controls-shell ${
          controlsVisible ? '' : 'is-hidden'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <ConferenceControls
          onClose={onClose}
          onTogglePanel={togglePanel}
          showPanel={panel}
        />
      </div>

      {(panel === 'participants' || panel === 'chat') && (
        <SidePanel
          panel={panel}
          participants={participants}
          onClose={() => setPanel(null)}
          onChangePanel={setPanel}
        />
      )}

      {panel === 'more' && <MorePanel onClose={() => setPanel(null)} />}
    </div>
  );
};

const ConferenceLiveKit = ({ open, onClose, token, serverUrl }) => {
  if (!open || !token || !serverUrl) {
    return null;
  }

  // Render directly under document.body so site-level stacking contexts or
  // transformed app wrappers cannot leave the global navigation above the room.
  return createPortal(
    <div
      className="finconf-fullscreen-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        width: '100vw',
        height: '100dvh',
        backgroundColor: '#08090c',
        isolation: 'isolate',
      }}
    >
      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        connect={true}
        video={true}
        audio={true}
        onDisconnected={onClose}
        data-lk-theme="default"
        style={{
          height: '100%',
          width: '100%',
        }}
      >
        <FinCollabConference onClose={onClose} />
      </LiveKitRoom>
    </div>,
    document.body
  );
};

export default ConferenceLiveKit;
